/**
 * 예산변경 PoC (등록·승인)
 * - 확정예산 기준 부서·사업·과목·재원 변경
 * - 매칭: ONE_TO_ONE | N_TO_N (기관 환경설정)
 * - 배정처리 Y: 승인 시 배정처리 테이블에 changeNo로 추적 (변경 마스터에 배정완료YN 없음)
 * - 배정처리 N: 배정은 예산배정 화면에서 별도
 *
 * 키: erp.budget.change|{tenant}|{fy}|{kind}|{inout}
 */

import { isBdApiEnabled } from 'config/bdApiConfig';
import { commonSelect, commonSaveList, cacheGet, cacheSet } from 'services/commonQuery';

export const CHANGE_STORAGE_PREFIX = 'erp.budget.change';

const CHANGE_SAVE_QUERY = [
  'BdBudgetExec.insertChange',
  'BdBudgetExec.updateChange',
  'BdBudgetExec.deleteChange',
];
const CHANGE_DETAIL_SAVE_QUERY = [
  'BdBudgetExec.insertChangeDetail',
  'BdBudgetExec.updateChangeDetail',
  'BdBudgetExec.deleteChangeDetail',
];

const changeCache = new Map();

export const CHANGE_MATCH_MODE = {
  ONE_TO_ONE: 'ONE_TO_ONE',
  N_TO_N: 'N_TO_N',
};

export const CHANGE_MATCH_MODE_OPTIONS = [
  { value: CHANGE_MATCH_MODE.ONE_TO_ONE, label: '1:1 매칭 (변경전→변경후 1행)' },
  { value: CHANGE_MATCH_MODE.N_TO_N, label: 'N:N 매칭 (축별 ±증감, 합계 0)' },
];

/** 변경구분 */
export const CHANGE_TYPE = {
  CHANGE: 'CHANGE',
  ADJUST: 'ADJUST',
  DEDICATED: 'DEDICATED',
  USE: 'USE',
  TRANSFER: 'TRANSFER',
  RESERVE: 'RESERVE',
};

export const CHANGE_TYPE_OPTIONS = [
  { value: CHANGE_TYPE.CHANGE, label: '변경' },
  { value: CHANGE_TYPE.ADJUST, label: '조정' },
  { value: CHANGE_TYPE.DEDICATED, label: '전용' },
  { value: CHANGE_TYPE.USE, label: '이용' },
  { value: CHANGE_TYPE.TRANSFER, label: '이체' },
  { value: CHANGE_TYPE.RESERVE, label: '예비비사용' },
];

export const CHANGE_STATUS = {
  DRAFT: 'DRAFT',
  REQUESTED: 'REQUESTED',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
};

export const CHANGE_STATUS_OPTIONS = [
  { value: CHANGE_STATUS.DRAFT, label: '작성중' },
  { value: CHANGE_STATUS.REQUESTED, label: '승인요청' },
  { value: CHANGE_STATUS.APPROVED, label: '승인' },
  { value: CHANGE_STATUS.REJECTED, label: '반려' },
];

export const ASSIGN_PROCESS_OPTIONS = [
  { value: 'Y', label: 'Y (승인 시 배정까지)' },
  { value: 'N', label: 'N (배정 별도)' },
];

function createUid(prefix = 'CHG') {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

export function createChangeUid() {
  return createUid('CHG');
}

export function createChangeDetailUid() {
  return createUid('CHGD');
}

export function changeTypeLabel(code) {
  return CHANGE_TYPE_OPTIONS.find((o) => o.value === code)?.label || code || '';
}

export function changeStatusLabel(code) {
  return CHANGE_STATUS_OPTIONS.find((o) => o.value === code)?.label || code || '';
}

export function changeMatchModeLabel(code) {
  return CHANGE_MATCH_MODE_OPTIONS.find((o) => o.value === code)?.label || code || '';
}

function dimsKey(dims = {}) {
  return [
    CHANGE_STORAGE_PREFIX,
    String(dims.tenantCode || '').trim(),
    String(dims.fiscalYear || '').trim(),
    String(dims.budgetKindCode || '').trim(),
    String(dims.inoutDiv || '지출').trim(),
  ].join('|');
}

function readBundle(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { masters: [], details: [] };
    const data = JSON.parse(raw);
    return {
      masters: Array.isArray(data?.masters) ? data.masters : [],
      details: Array.isArray(data?.details) ? data.details : [],
    };
  } catch {
    return { masters: [], details: [] };
  }
}

function writeBundle(key, masters, details) {
  localStorage.setItem(
    key,
    JSON.stringify({
      masters: masters || [],
      details: details || [],
      savedAt: new Date().toISOString(),
    })
  );
}

function changeQueryDims(dims = {}) {
  return {
    tenantCode: dims.tenantCode,
    fiscalYear: dims.fiscalYear,
    budgetKindCode: dims.budgetKindCode,
    inoutDiv: dims.inoutDiv || '지출',
  };
}

function mapChangeMasterFromApi(row) {
  return normalizeMasterDates({
    ...row,
    mod: '',
  });
}

function mapChangeDetailFromApi(row) {
  return {
    ...row,
    mod: '',
  };
}

function changeDetailPersistPayload(d, dims) {
  return {
    detailUid: d.detailUid,
    fiscalYear: dims.fiscalYear,
    changeUid: d.changeUid,
    beforeBalanceUid: d.beforeBalanceUid || null,
    beforeBalanceAmt: d.beforeBalanceAmt != null ? Number(d.beforeBalanceAmt) : null,
    beforeDeptCd: d.beforeDeptCd || null,
    beforeDeptNm: d.beforeDeptNm || null,
    beforeProjectCd: d.beforeProjectCd || null,
    beforeProjectNm: d.beforeProjectNm || null,
    beforeSubjectCd: d.beforeSubjectCd || null,
    beforeSubjectNm: d.beforeSubjectNm || null,
    beforeFundCd: d.beforeFundCd || null,
    beforeFundNm: d.beforeFundNm || null,
    afterBalanceUid: d.afterBalanceUid || null,
    afterBalanceAmt: d.afterBalanceAmt != null ? Number(d.afterBalanceAmt) : null,
    afterDeptCd: d.afterDeptCd || null,
    afterDeptNm: d.afterDeptNm || null,
    afterProjectCd: d.afterProjectCd || null,
    afterProjectNm: d.afterProjectNm || null,
    afterSubjectCd: d.afterSubjectCd || null,
    afterSubjectNm: d.afterSubjectNm || null,
    afterFundCd: d.afterFundCd || null,
    afterFundNm: d.afterFundNm || null,
    balanceUid: d.balanceUid || null,
    balanceAmt: d.balanceAmt != null ? Number(d.balanceAmt) : null,
    deptCd: d.deptCd || null,
    deptNm: d.deptNm || null,
    projectCd: d.projectCd || null,
    projectNm: d.projectNm || null,
    subjectCd: d.subjectCd || null,
    subjectNm: d.subjectNm || null,
    fundCd: d.fundCd || null,
    fundNm: d.fundNm || null,
    changeAmt: Number(d.changeAmt) || 0,
  };
}

export async function loadChangeBundle(dims) {
  const key = dimsKey(dims);
  if (isBdApiEnabled()) {
    const addValue = changeQueryDims(dims);
    const masters = (await commonSelect('BdBudgetExec.selectChangeList', addValue)).map(
      mapChangeMasterFromApi
    );
    const details = (await commonSelect('BdBudgetExec.selectChangeDetailList', addValue)).map(
      mapChangeDetailFromApi
    );
    const bundle = { masters, details };
    cacheSet(changeCache, key, bundle);
    return bundle;
  }
  const bundle = readBundle(key);
  cacheSet(changeCache, key, bundle);
  return bundle;
}

export async function saveChangeBundle(dims, masters, details) {
  const key = dimsKey(dims);
  const activeMasters = (masters || []).filter((m) => m.mod !== 'D');
  const activeDetails = (details || []).filter((d) => d.mod !== 'D');

  if (isBdApiEnabled()) {
    const addValue = changeQueryDims(dims);
    const dbMasters = await commonSelect('BdBudgetExec.selectChangeList', addValue);
    const dbDetails = await commonSelect('BdBudgetExec.selectChangeDetailList', addValue);
    const dbMasterUids = new Set(dbMasters.map((m) => m.changeUid));
    const dbDetailUids = new Set(dbDetails.map((d) => d.detailUid));

    const masterDeleted = (masters || [])
      .filter((m) => m.mod === 'D' && m.changeUid && dbMasterUids.has(m.changeUid));
    for (const m of masterDeleted) {
      const childDeletes = dbDetails
        .filter((d) => d.changeUid === m.changeUid)
        .map((d) => ({ detailUid: d.detailUid, mod: 'D' }));
      if (childDeletes.length) {
        await commonSaveList(CHANGE_DETAIL_SAVE_QUERY, childDeletes, addValue);
      }
    }
    if (masterDeleted.length) {
      await commonSaveList(
        CHANGE_SAVE_QUERY,
        masterDeleted.map((m) => ({ changeUid: m.changeUid, mod: 'D' })),
        addValue
      );
    }

    const masterPayload = activeMasters.map((m) => ({
      changeUid: m.changeUid,
      changeNo: m.changeNo || null,
      changeType: m.changeType || CHANGE_TYPE.CHANGE,
      assignProcessYn: m.assignProcessYn || 'N',
      changeAmt: Number(m.changeAmt) || 0,
      approveAmt: Number(m.approveAmt) || 0,
      changeDate: m.changeDate || null,
      registerDate: m.registerDate || null,
      approveDate: m.approveDate || null,
      remark: m.remark || null,
      status: m.status || CHANGE_STATUS.DRAFT,
      matchMode: m.matchMode || CHANGE_MATCH_MODE.ONE_TO_ONE,
      requestAt: m.requestAt || null,
      rejectReason: m.rejectReason || null,
      mod: dbMasterUids.has(m.changeUid) ? 'U' : 'A',
    }));
    if (masterPayload.length) await commonSaveList(CHANGE_SAVE_QUERY, masterPayload, addValue);

    const detailDeleted = (details || [])
      .filter((d) => d.mod === 'D' && d.detailUid && dbDetailUids.has(d.detailUid))
      .map((d) => ({ detailUid: d.detailUid, mod: 'D' }));
    if (detailDeleted.length) {
      await commonSaveList(CHANGE_DETAIL_SAVE_QUERY, detailDeleted, addValue);
    }

    const keepDetailUids = new Set(activeDetails.map((d) => d.detailUid));
    const orphanDetails = dbDetails
      .filter((d) => !keepDetailUids.has(d.detailUid))
      .map((d) => ({ detailUid: d.detailUid, mod: 'D' }));
    if (orphanDetails.length) {
      await commonSaveList(CHANGE_DETAIL_SAVE_QUERY, orphanDetails, addValue);
    }

    const detailPayload = activeDetails.map((d) => ({
      ...changeDetailPersistPayload(d, dims),
      mod: dbDetailUids.has(d.detailUid) ? 'U' : 'A',
    }));
    if (detailPayload.length) {
      await commonSaveList(CHANGE_DETAIL_SAVE_QUERY, detailPayload, addValue);
    }

    return loadChangeBundle(dims);
  }

  writeBundle(key, activeMasters, activeDetails);
  const bundle = { masters: activeMasters, details: activeDetails };
  cacheSet(changeCache, key, bundle);
  return bundle;
}

/**
 * 마스터 변경금액
 * - 1:1: 디테일 changeAmt 합
 * - N:N: 증액(+) 합 (= |감액 합|, 검증 통과 시)
 */
export function calcMasterChangeAmt(master, details, matchMode) {
  const list = (details || []).filter((d) => d.changeUid === master.changeUid && d.mod !== 'D');
  if (matchMode === CHANGE_MATCH_MODE.N_TO_N) {
    return list
      .map((d) => Number(d.changeAmt) || 0)
      .filter((n) => n > 0)
      .reduce((s, n) => s + n, 0);
  }
  return list.reduce((s, d) => s + (Number(d.changeAmt) || 0), 0);
}

export function emptyMasterRow(partial = {}) {
  return {
    changeUid: '',
    changeNo: '',
    changeType: CHANGE_TYPE.CHANGE,
    assignProcessYn: 'N',
    changeAmt: 0,
    approveAmt: 0,
    /** 업무 변경일자 — 사용자 캘린더 입력(소급 가능). 오늘 자동채움 금지 */
    changeDate: '',
    /** 시스템 등록일자 — 최초 저장 시 세팅, 이후 수정 불가 */
    registerDate: '',
    /** 승인일자 — 승인 처리 시 세팅. 등록·승인 화면 모두 표시 */
    approveDate: '',
    remark: '',
    status: CHANGE_STATUS.DRAFT,
    matchMode: CHANGE_MATCH_MODE.ONE_TO_ONE,
    requestAt: '',
    rejectReason: '',
    mod: '',
    ...partial,
  };
}

/** 오늘 YYYY-MM-DD (등록·승인 시스템 일자용) */
export function todayYmd(d = new Date()) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** 구 필드(changeAt 등) → 신규 일자 필드 보정 */
export function normalizeMasterDates(row = {}) {
  if (!row) return row;
  let changeDate = String(row.changeDate || '').trim();
  if (!changeDate && row.changeAt) {
    changeDate = String(row.changeAt).slice(0, 10);
  }
  let registerDate = String(row.registerDate || '').trim();
  if (!registerDate && row.registeredAt) {
    registerDate = String(row.registeredAt).slice(0, 10);
  }
  let approveDate = String(row.approveDate || '').trim();
  if (!approveDate && row.approveAt) {
    approveDate = String(row.approveAt).slice(0, 10);
  }
  return { ...row, changeDate, registerDate, approveDate };
}

/** 1:1 디테일 — 변경전/후 축 + 이동액 + 현액(참고·팝업적재) */
export function emptyDetailOneToOne(partial = {}) {
  return {
    detailUid: '',
    changeUid: '',
    beforeBalanceUid: '',
    beforeBalanceAmt: null,
    beforeDeptCd: '',
    beforeDeptNm: '',
    beforeProjectCd: '',
    beforeProjectNm: '',
    beforeSubjectCd: '',
    beforeSubjectNm: '',
    beforeFundCd: '',
    beforeFundNm: '',
    afterBalanceUid: '',
    afterBalanceAmt: null,
    afterDeptCd: '',
    afterDeptNm: '',
    afterProjectCd: '',
    afterProjectNm: '',
    afterSubjectCd: '',
    afterSubjectNm: '',
    afterFundCd: '',
    afterFundNm: '',
    changeAmt: 0,
    mod: '',
    ...partial,
  };
}

/** N:N 디테일 — 축 1세트 + 부호 있는 증감액(합계 0) + 현액 */
export function emptyDetailNn(partial = {}) {
  return {
    detailUid: '',
    changeUid: '',
    balanceUid: '',
    balanceAmt: null,
    deptCd: '',
    deptNm: '',
    projectCd: '',
    projectNm: '',
    subjectCd: '',
    subjectNm: '',
    fundCd: '',
    fundNm: '',
    changeAmt: 0,
    mod: '',
    ...partial,
  };
}

export function nextChangeNo(masters) {
  const nums = (masters || [])
    .map((m) => Number(String(m.changeNo || '').replace(/\D/g, '')) || 0)
    .filter((n) => n > 0);
  const max = nums.length ? Math.max(...nums) : 0;
  return String(max + 1).padStart(4, '0');
}

/**
 * 저장 전 검증
 * @returns {{ ok: boolean, message?: string }}
 */
export function validateChangeBundle(masters, details, matchMode) {
  const activeM = (masters || []).filter((m) => m.mod !== 'D');
  for (const m of activeM) {
    if (!m.changeType) {
      return { ok: false, message: '변경구분을 선택하세요.' };
    }
    if (m.assignProcessYn !== 'Y' && m.assignProcessYn !== 'N') {
      return { ok: false, message: '배정처리 Y/N을 선택하세요.' };
    }
    if (!String(m.changeDate || '').trim()) {
      return {
        ok: false,
        message: `변경번호 ${m.changeNo || ''}: 변경일자를 캘린더에서 지정하세요. (소급 적용 가능 · 오늘 자동입력 없음)`,
      };
    }
    const dets = (details || []).filter((d) => d.changeUid === m.changeUid && d.mod !== 'D');
    if (!dets.length && m.status !== CHANGE_STATUS.DRAFT) {
      return { ok: false, message: `변경번호 ${m.changeNo || m.changeUid}: 디테일이 없습니다.` };
    }
    if (matchMode === CHANGE_MATCH_MODE.N_TO_N && dets.length) {
      const sum = dets.reduce((s, d) => s + (Number(d.changeAmt) || 0), 0);
      if (Math.abs(sum) > 0.0001) {
        return {
          ok: false,
          message: `변경번호 ${m.changeNo}: N:N 매칭은 증감 합계가 0이어야 합니다. (현재 ${sum})`,
        };
      }
    }
  }
  return { ok: true };
}

/** 승인요청 — 변경일자는 사용자가 이미 입력한 값 유지 */
export function requestApproval(masters, details, changeUid) {
  const now = new Date().toISOString();
  const nextMasters = (masters || []).map((m) => {
    if (m.changeUid !== changeUid) return m;
    if (m.status !== CHANGE_STATUS.DRAFT && m.status !== CHANGE_STATUS.REJECTED) {
      return m;
    }
    return {
      ...m,
      status: CHANGE_STATUS.REQUESTED,
      requestAt: now,
      mod: m.mod === 'A' ? 'A' : 'U',
    };
  });
  return { masters: nextMasters, details: details || [] };
}

/**
 * 승인 — approveDate는 시스템 오늘(YYYY-MM-DD). 배정은 allocProcess.changeNo로 추적
 */
export function approveChange(masters, details, changeUid, { approveAmt, approveDate } = {}) {
  const now = new Date().toISOString();
  const ymd = approveDate || todayYmd();
  const nextMasters = (masters || []).map((m) => {
    if (m.changeUid !== changeUid) return m;
    if (m.status !== CHANGE_STATUS.REQUESTED) return m;
    const amt = approveAmt != null ? Number(approveAmt) : Number(m.changeAmt) || 0;
    return {
      ...m,
      status: CHANGE_STATUS.APPROVED,
      approveAmt: amt,
      approveDate: ymd,
      mod: m.mod === 'A' ? 'A' : 'U',
    };
  });
  return { masters: nextMasters, details: details || [] };
}

export function rejectChange(masters, details, changeUid, reason = '') {
  const nextMasters = (masters || []).map((m) => {
    if (m.changeUid !== changeUid) return m;
    if (m.status !== CHANGE_STATUS.REQUESTED) return m;
    return {
      ...m,
      status: CHANGE_STATUS.REJECTED,
      rejectReason: String(reason || '').trim(),
      // 반려 시 승인일자는 비움(승인 처리가 아님)
      approveDate: '',
      mod: m.mod === 'A' ? 'A' : 'U',
    };
  });
  return { masters: nextMasters, details: details || [] };
}

/** 승인 화면 조회용 — 요청·승인·반려 */
export function filterMastersForApprove(masters, { status } = {}) {
  const list = (masters || []).filter((m) => m.mod !== 'D');
  if (status) return list.filter((m) => m.status === status);
  return list.filter((m) =>
    [CHANGE_STATUS.REQUESTED, CHANGE_STATUS.APPROVED, CHANGE_STATUS.REJECTED].includes(m.status)
  );
}

/** 등록 화면 — 전체(작성중 포함) */
export function filterMastersForRegister(masters, { status } = {}) {
  const list = (masters || []).filter((m) => m.mod !== 'D');
  if (status) return list.filter((m) => m.status === status);
  return list;
}

/**
 * 배정연동용 라인 목록 (배정처리=Y 승인 시)
 * - 1:1: 변경후 축 + 이동액(+)
 * - N:N: 축 + 부호 있는 증감액
 */
export function buildAllocLinesFromChange(master, details, matchMode) {
  const list = (details || []).filter((d) => d.changeUid === master.changeUid && d.mod !== 'D');
  const changeNo = String(master.changeNo || '').trim();
  const changeUid = master.changeUid;

  if (matchMode === CHANGE_MATCH_MODE.N_TO_N) {
    return list.map((d) => ({
      changeNo,
      changeUid,
      changeDetailUid: d.detailUid,
      deptCode: d.deptCd || '',
      deptName: d.deptNm || '',
      projectCode: d.projectCd || '',
      projectName: d.projectNm || '',
      subjectCode: d.subjectCd || '',
      subjectName: d.subjectNm || '',
      fundCode: d.fundCd || '',
      fundName: d.fundNm || '',
      changeAmt: Number(d.changeAmt) || 0,
    }));
  }

  return list.map((d) => ({
    changeNo,
    changeUid,
    changeDetailUid: d.detailUid,
    deptCode: d.afterDeptCd || '',
    deptName: d.afterDeptNm || '',
    projectCode: d.afterProjectCd || '',
    projectName: d.afterProjectNm || '',
    subjectCode: d.afterSubjectCd || '',
    subjectName: d.afterSubjectNm || '',
    fundCode: d.afterFundCd || '',
    fundName: d.afterFundNm || '',
    changeAmt: Number(d.changeAmt) || 0,
  }));
}
